<template>
  <div class="clock-time-picker" :class="{ 'is-disabled': disabled }">
    <div class="ctp-tabs">
      <input
        ref="hourInput"
        type="text"
        inputmode="numeric"
        maxlength="2"
        class="ctp-tab-input"
        :class="{ active: mode === 'hour' }"
        :disabled="disabled"
        :value="editingField === 'hour' ? draftText : hourLabel"
        @focus="onFocusField('hour')"
        @input="onInputField('hour', $event)"
        @blur="onBlurField('hour')"
        @keydown.enter.prevent="onEnterField($event)"
      >
      <span class="ctp-tabs-sep">:</span>
      <input
        ref="minuteInput"
        type="text"
        inputmode="numeric"
        maxlength="2"
        class="ctp-tab-input"
        :class="{ active: mode === 'minute' }"
        :disabled="disabled"
        :value="editingField === 'minute' ? draftText : minuteLabel"
        @focus="onFocusField('minute')"
        @input="onInputField('minute', $event)"
        @blur="onBlurField('minute')"
        @keydown.enter.prevent="onEnterField($event)"
      >
    </div>
    <svg
      class="ctp-dial"
      viewBox="0 0 200 200"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <circle cx="100" cy="100" r="98" class="ctp-face" />
      <line :x1="100" :y1="100" :x2="handle.x" :y2="handle.y" class="ctp-hand" />
      <circle :cx="handle.x" :cy="handle.y" r="14" class="ctp-handle-knob" />
      <g v-if="mode === 'hour'">
        <text
          v-for="opt in hourPositions"
          :key="`h-${opt.value}`"
          :x="opt.x"
          :y="opt.y"
          class="ctp-number"
          :class="{ active: value.hours === opt.value }"
        >{{ opt.label }}</text>
      </g>
      <g v-else>
        <line
          v-for="tick in minuteTicks"
          :key="`t-${tick.value}`"
          :x1="tick.x1" :y1="tick.y1" :x2="tick.x2" :y2="tick.y2"
          class="ctp-tick"
        />
        <text
          v-for="opt in minutePositions"
          :key="`m-${opt.value}`"
          :x="opt.x"
          :y="opt.y"
          class="ctp-number ctp-minute-number"
          :class="{ active: value.minutes === opt.value }"
        >{{ opt.label }}</text>
      </g>
      <circle cx="100" cy="100" r="4" class="ctp-center" />
    </svg>
  </div>
</template>

<script>
export default {
  name: 'ClockTimePicker',
  props: {
    value: {
      type: Object,
      default: () => ({ hours: 0, minutes: 0 }),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: 'hour',
      dragging: false,
      outerRadius: 78,
      innerRadius: 48,
      minuteRadius: 78,
      editingField: null,
      draftText: '',
    };
  },
  computed: {
    hourLabel() {
      return String(this.value.hours).padStart(2, '0');
    },
    minuteLabel() {
      return String(this.value.minutes).padStart(2, '0');
    },
    hourPositions() {
      const positions = [];
      for (let i = 0; i < 12; i += 1) {
        const angle = this.degToRad(i * 30);
        positions.push({
          value: i,
          label: String(i),
          x: 100 + this.outerRadius * Math.sin(angle),
          y: 100 - this.outerRadius * Math.cos(angle) + 4,
        });
        positions.push({
          value: i + 12,
          label: String(i + 12),
          x: 100 + this.innerRadius * Math.sin(angle),
          y: 100 - this.innerRadius * Math.cos(angle) + 4,
        });
      }
      return positions;
    },
    minutePositions() {
      const positions = [];
      for (let m = 0; m < 60; m += 5) {
        const angle = this.degToRad(m * 6);
        positions.push({
          value: m,
          label: String(m).padStart(2, '0'),
          x: 100 + this.minuteRadius * Math.sin(angle),
          y: 100 - this.minuteRadius * Math.cos(angle) + 4,
        });
      }
      return positions;
    },
    minuteTicks() {
      const ticks = [];
      for (let m = 0; m < 60; m += 1) {
        if (m % 5 !== 0) {
          const angle = this.degToRad(m * 6);
          const outer = this.minuteRadius + 6;
          const inner = this.minuteRadius - 2;
          ticks.push({
            value: m,
            x1: 100 + inner * Math.sin(angle),
            y1: 100 - inner * Math.cos(angle),
            x2: 100 + outer * Math.sin(angle),
            y2: 100 - outer * Math.cos(angle),
          });
        }
      }
      return ticks;
    },
    handle() {
      if (this.mode === 'hour') {
        const hour = this.value.hours;
        const angle = this.degToRad((hour % 12) * 30);
        const radius = hour < 12 ? this.outerRadius : this.innerRadius;
        return {
          x: 100 + radius * Math.sin(angle),
          y: 100 - radius * Math.cos(angle),
        };
      }
      const angle = this.degToRad(this.value.minutes * 6);
      return {
        x: 100 + this.minuteRadius * Math.sin(angle),
        y: 100 - this.minuteRadius * Math.cos(angle),
      };
    },
  },
  methods: {
    degToRad(deg) {
      return (deg * Math.PI) / 180;
    },
    angleFromPoint(x, y) {
      let deg = (Math.atan2(y - 100, x - 100) * 180) / Math.PI + 90;
      if (deg < 0) deg += 360;
      return deg;
    },
    toSvgPoint(evt) {
      const svg = this.$el.querySelector('svg');
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    },
    handlePointer(evt) {
      const { x, y } = this.toSvgPoint(evt);
      const deg = this.angleFromPoint(x, y);
      if (this.mode === 'hour') {
        const dist = Math.hypot(x - 100, y - 100);
        const ringIndex = Math.round(deg / 30) % 12;
        const isOuter = Math.abs(dist - this.outerRadius) < Math.abs(dist - this.innerRadius);
        this.updateHours(isOuter ? ringIndex : ringIndex + 12);
      } else {
        const minute = Math.round(deg / 6) % 60;
        this.updateMinutes(minute);
      }
    },
    updateHours(hour) {
      if (hour === this.value.hours) return;
      this.$emit('input', { hours: hour, minutes: this.value.minutes });
    },
    updateMinutes(minute) {
      if (minute === this.value.minutes) return;
      this.$emit('input', { hours: this.value.hours, minutes: minute });
    },
    onPointerDown(evt) {
      if (this.disabled) return;
      this.dragging = true;
      evt.target.closest('svg').setPointerCapture(evt.pointerId);
      this.handlePointer(evt);
    },
    onPointerMove(evt) {
      if (!this.dragging) return;
      this.handlePointer(evt);
    },
    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.mode === 'hour') {
        this.mode = 'minute';
      } else {
        this.$emit('commit');
      }
    },
    onFocusField(field) {
      if (this.disabled) return;
      this.mode = field;
      this.editingField = field;
      this.draftText = field === 'hour' ? this.hourLabel : this.minuteLabel;
      this.$nextTick(() => {
        const input = field === 'hour' ? this.$refs.hourInput : this.$refs.minuteInput;
        if (input) input.select();
      });
    },
    onInputField(field, evt) {
      const digits = evt.target.value.replace(/\D/g, '').slice(0, 2);
      evt.target.value = digits;
      this.draftText = digits;
      if (digits.length === 2 && field === 'hour') {
        this.commitField('hour');
        this.$nextTick(() => {
          if (this.$refs.minuteInput) this.$refs.minuteInput.focus();
        });
      }
    },
    onBlurField(field) {
      this.commitField(field);
      this.editingField = null;
      this.draftText = '';
      if (field === 'minute') this.$emit('commit');
    },
    commitField(field) {
      const max = field === 'hour' ? 23 : 59;
      const num = this.draftText === '' ? 0 : Math.min(parseInt(this.draftText, 10), max);
      if (field === 'hour') this.updateHours(num); else this.updateMinutes(num);
    },
    onEnterField(evt) {
      evt.target.blur();
    },
  },
};
</script>

<style scoped>
.clock-time-picker {
  width: 220px;
  user-select: none;
}
.clock-time-picker.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}
.ctp-tabs {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
}
.ctp-tab-input {
  background: transparent;
  border: none;
  padding: 2px 6px;
  border-radius: 4px;
  color: #6c757d;
  font: inherit;
  width: calc(2ch + 12px);
  text-align: center;
}
.ctp-tab-input.active {
  color: #ff69b4;
}
.ctp-tab-input:focus {
  outline: none;
  background: #f4f4f6;
}
.ctp-tabs-sep {
  color: #6c757d;
}
.ctp-dial {
  width: 100%;
  height: auto;
  touch-action: none;
}
.ctp-face {
  fill: #f4f4f6;
  stroke: #e2e2e6;
}
.ctp-hand {
  stroke: #ff69b4;
  stroke-width: 2;
}
.ctp-center {
  fill: #ff69b4;
}
.ctp-handle-knob {
  fill: #ff69b4;
  opacity: 0.85;
}
.ctp-tick {
  stroke: #c7c7cf;
  stroke-width: 1;
}
.ctp-number {
  font-size: 13px;
  text-anchor: middle;
  fill: #495057;
}
.ctp-minute-number {
  font-size: 11px;
}
.ctp-number.active {
  fill: #fff;
  font-weight: 700;
}
</style>
